import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../../services/apiService';
import Dialogo from '../../components/Dialogo';
import '../MetasConfig.css';
import './Escalas.css';
import {
    DIAS_SEMANA, ajustarCiclo, exigirForma, mensagemErro, padraoParaForm, padraoParaPayload, padraoVazio,
    previaPadrao, resumoCiclo, resumoHorario, validarPadrao, viraMeiaNoite,
} from './escalasUtil';

/**
 * Aba Padrões: o catálogo de escalas da empresa (5x2, shopping 6x1, 12x36…).
 *
 * Padrão EM USO não muda de ciclo, dias nem regra de feriado: isso reescreveria o passado de quem
 * já usa. A API devolve 409 e a tela já nem oferece o campo; para mudar daqui para frente o caminho
 * é "Duplicar como novo" e atribuir a partir da data (aba Atribuições).
 */
function PadroesTab({ subdomain, notify }) {
    const [padroes, setPadroes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [erroApi, setErroApi] = useState('');
    const [paraExcluir, setParaExcluir] = useState(null);
    const savingRef = useRef(false);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const r = exigirForma(await apiService.scheduleListPatterns({ subdomain }), 'patterns');
            setPadroes(r.patterns);
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao carregar os padrões'), 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, notify]);

    useEffect(() => { setForm(null); setPadroes([]); carregar(); }, [carregar]);

    const emUso = !!(form && form.id && form.assignments_in_use > 0);
    const erro = form ? validarPadrao(form) : null;

    const setCampo = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const setDia = (idx, k, v) => setForm((p) => ({
        ...p,
        days: p.days.map((d) => (d.day_index === idx ? { ...d, [k]: v } : d)),
    }));
    const marcarTrabalho = (idx, trabalha) => setForm((p) => ({
        ...p,
        days: p.days.map((d) => (d.day_index !== idx ? d : {
            ...d,
            is_working: trabalha,
            start_time: trabalha ? (d.start_time || '09:00') : '',
            end_time: trabalha ? (d.end_time || '18:00') : '',
            break_minutes: trabalha ? (d.break_minutes || 0) : 0,
        })),
    }));
    const copiarPrimeiro = () => setForm((p) => {
        const ref = p.days.find((d) => d.is_working);
        if (!ref) return p;
        return {
            ...p,
            days: p.days.map((d) => (d.is_working
                ? { ...d, start_time: ref.start_time, end_time: ref.end_time, break_minutes: ref.break_minutes }
                : d)),
        };
    });

    const abrirNovo = () => { setErroApi(''); setForm(padraoVazio()); };
    const abrirEdicao = (p) => { setErroApi(''); setForm(padraoParaForm(p)); };
    const duplicar = () => {
        setErroApi('');
        setForm((p) => ({ ...p, id: null, name: `${p.name} (cópia)`, assignments_in_use: 0 }));
    };
    const fechar = () => { setForm(null); setErroApi(''); };

    const salvar = async () => {
        if (erro) { notify(erro, 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setErroApi('');
        try {
            const r = await apiService.scheduleSavePattern(padraoParaPayload(form, subdomain));
            if (!r || r.success !== true) throw new Error('resposta fora de forma');
            notify(form.id ? 'Padrão atualizado' : 'Padrão criado', 'success');
            fechar();
            carregar();
        } catch (e) {
            setErroApi(mensagemErro(e, 'Erro ao salvar o padrão'));
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const excluir = async () => {
        const p = paraExcluir;
        try {
            await apiService.scheduleDeletePattern({ subdomain, id: p.id });
            notify('Padrão excluído', 'success');
            setParaExcluir(null);
            carregar();
        } catch (e) {
            notify(mensagemErro(e, 'Erro ao excluir o padrão'), 'error');
            setParaExcluir(null);
        }
    };

    const nomeDia = (d) => (form.cycle_type === 'weekly' ? DIAS_SEMANA[d.day_index] : `Dia ${d.day_index + 1}`);

    return (
        <div className="mc-page">
            <p className="mc-nota">
                Um <strong>padrão</strong> descreve o ciclo de trabalho: semanal (5x2 de segunda a sexta) ou
                rotativo (6x1, 12x36, 4x2). Quem usa qual padrão, e a partir de quando, se define na aba
                <strong> Atribuições</strong>. Padrão em uso não muda de ciclo nem de dias: crie outro.
            </p>

            <div className="mc-filters">
                <div className="mc-field mc-filters-actions esc-acoes">
                    <button className="mc-btn mc-btn-primary" onClick={abrirNovo} disabled={!subdomain}>➕ Novo padrão</button>
                </div>
            </div>

            {form && (
                <div className="mc-form-panel">
                    <h2>{form.id ? 'Editar padrão' : 'Novo padrão'}</h2>
                    {emUso && (
                        <p className="esc-aviso">
                            Este padrão está em uso por <strong>{form.assignments_in_use}</strong> atribuição(ões):
                            só o nome e “Ativo” podem mudar. Para mudar ciclo, dias ou a regra de feriado, use
                            <strong> Duplicar como novo</strong> e atribua o novo padrão a partir da data.
                        </p>
                    )}
                    <div className="mc-form-grid">
                        <div className="mc-field">
                            <label htmlFor="esc-pad-nome">Nome *</label>
                            <input id="esc-pad-nome" type="text" value={form.name} onChange={(e) => setCampo('name', e.target.value)} />
                        </div>
                        <div className="mc-field">
                            <label htmlFor="esc-pad-tipo">Ciclo *</label>
                            <select
                                id="esc-pad-tipo"
                                value={form.cycle_type}
                                disabled={emUso}
                                onChange={(e) => setForm((p) => ajustarCiclo(p, e.target.value, 7))}
                            >
                                <option value="weekly">Semanal (dias da semana)</option>
                                <option value="rotating">Rotativo (6x1, 12x36…)</option>
                            </select>
                        </div>
                        {form.cycle_type === 'rotating' && (
                            <div className="mc-field">
                                <label htmlFor="esc-pad-len">Dias do ciclo * (2 a 60)</label>
                                <input
                                    id="esc-pad-len"
                                    type="number"
                                    min="2"
                                    max="60"
                                    value={form.cycle_length}
                                    disabled={emUso}
                                    onChange={(e) => setForm((p) => ajustarCiclo(p, 'rotating', e.target.value))}
                                />
                            </div>
                        )}
                        <div className="mc-field">
                            <label>
                                <input type="checkbox" checked={form.works_on_holidays} disabled={emUso}
                                    onChange={(e) => setCampo('works_on_holidays', e.target.checked)} /> Trabalha em feriado nacional
                            </label>
                            <small className="esc-dica">Shopping costuma marcar. Com isso o feriado não vira folga.</small>
                        </div>
                        <div className="mc-field">
                            <label>
                                <input type="checkbox" checked={form.active} onChange={(e) => setCampo('active', e.target.checked)} /> Ativo
                            </label>
                            <small className="esc-dica">Padrão inativo não aparece para novas atribuições.</small>
                        </div>
                    </div>

                    <div className="esc-grade-topo">
                        <h3>Dias do ciclo</h3>
                        <button className="mc-btn mc-btn-sm" onClick={copiarPrimeiro} disabled={emUso}>
                            Copiar o 1º dia de trabalho para os demais
                        </button>
                    </div>
                    <div className="esc-tabela-rolagem">
                        <table className="mc-table esc-grade">
                            <thead>
                                <tr><th>Dia</th><th>Trabalha</th><th>Início</th><th>Fim</th><th>Intervalo (min)</th><th></th></tr>
                            </thead>
                            <tbody>
                                {form.days.map((d) => (
                                    <tr key={d.day_index} className={d.is_working ? '' : 'esc-linha-folga'}>
                                        <td>{nomeDia(d)}</td>
                                        <td>
                                            <input type="checkbox" checked={d.is_working} disabled={emUso}
                                                aria-label={`${nomeDia(d)}: trabalha`}
                                                onChange={(e) => marcarTrabalho(d.day_index, e.target.checked)} />
                                        </td>
                                        <td>
                                            <input type="time" value={d.start_time} disabled={emUso || !d.is_working}
                                                aria-label={`${nomeDia(d)}: início`}
                                                onChange={(e) => setDia(d.day_index, 'start_time', e.target.value)} />
                                        </td>
                                        <td>
                                            <input type="time" value={d.end_time} disabled={emUso || !d.is_working}
                                                aria-label={`${nomeDia(d)}: fim`}
                                                onChange={(e) => setDia(d.day_index, 'end_time', e.target.value)} />
                                        </td>
                                        <td>
                                            <input type="number" min="0" className="esc-num" value={d.break_minutes}
                                                disabled={emUso || !d.is_working}
                                                aria-label={`${nomeDia(d)}: intervalo`}
                                                onChange={(e) => setDia(d.day_index, 'break_minutes', e.target.value)} />
                                        </td>
                                        <td>
                                            {d.is_working && viraMeiaNoite(d.start_time, d.end_time) && (
                                                <span className="esc-chip esc-chip-noite">termina no dia seguinte</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h3>Prévia</h3>
                    <p className="esc-dica">{resumoCiclo(form)}</p>
                    <div className="esc-previa">
                        {previaPadrao(form).map((d) => (
                            <div key={d.rotulo} className={`esc-previa-dia ${d.trabalha ? 'esc-dia-work' : 'esc-dia-off'}`}>
                                <strong>{d.rotulo}</strong>
                                <span>{d.texto}</span>
                            </div>
                        ))}
                    </div>

                    {erro && <p className="mc-nota">{erro}</p>}
                    {erroApi && <p className="esc-erro" role="alert">{erroApi}</p>}
                    <div className="mc-form-actions">
                        {form.id && <button className="mc-btn" onClick={duplicar} disabled={saving}>Duplicar como novo</button>}
                        <button className="mc-btn" onClick={fechar} disabled={saving}>Cancelar</button>
                        <button className="mc-btn mc-btn-primary" onClick={salvar} disabled={saving || !!erro}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            )}

            <div className="mc-list">
                <h2>Padrões cadastrados {loading ? '(carregando…)' : `(${padroes.length})`}</h2>
                {!subdomain && <p className="mc-empty">Selecione uma empresa.</p>}
                {subdomain && !loading && padroes.length === 0 && (
                    <p className="mc-empty">Nenhum padrão cadastrado. Crie o primeiro (ex.: 5x2 comercial).</p>
                )}
                {padroes.length > 0 && (
                    <div className="esc-tabela-rolagem">
                        <table className="mc-table">
                            <thead>
                                <tr><th>Nome</th><th>Ciclo</th><th>Horário</th><th>Feriado</th><th>Em uso</th><th>Ativo</th><th></th></tr>
                            </thead>
                            <tbody>
                                {padroes.map((p) => {
                                    const ref = p.days.find((d) => d.is_working);
                                    const f = padraoParaForm(p);
                                    return (
                                        <tr key={p.id}>
                                            <td>{p.name}</td>
                                            <td>{p.cycle_type === 'weekly' ? 'Semanal' : `Rotativo de ${p.cycle_length} dias`}<br /><small>{resumoCiclo(f)}</small></td>
                                            <td>{ref ? resumoHorario(ref.start_time, ref.end_time) : '—'}</td>
                                            <td>{p.works_on_holidays ? 'Trabalha' : 'Folga'}</td>
                                            <td>{p.assignments_in_use}</td>
                                            <td>{p.active ? 'Sim' : 'Não'}</td>
                                            <td className="mc-actions">
                                                <button className="mc-btn mc-btn-sm" onClick={() => abrirEdicao(p)}>✏️ Editar</button>
                                                <button className="mc-btn mc-btn-sm mc-btn-danger" onClick={() => setParaExcluir(p)}
                                                    disabled={p.assignments_in_use > 0}
                                                    title={p.assignments_in_use > 0 ? 'Em uso: remova as atribuições antes' : ''}>
                                                    🗑️ Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {paraExcluir && (
                <Dialogo titulo="Excluir padrão" perigo rotuloConfirmar="Excluir"
                    onCancelar={() => setParaExcluir(null)} onConfirmar={excluir}>
                    <p>Excluir o padrão <strong>{paraExcluir.name}</strong>? Ele não aparece mais para novas atribuições.</p>
                </Dialogo>
            )}
        </div>
    );
}

export default PadroesTab;
